/**
 * ActivityPanel Component
 *
 * Right panel of the Collaboration section with three activity columns:
 * - SHARED WITH YOU: Documents shared with the user
 * - FILE ACTIVITY: Recent file activity
 * - NEW MEMBERS: Recent team member additions
 */

import { useState, useCallback } from 'react';
import { makeStyles, shorthands, Spinner, Tooltip } from '@fluentui/react-components';
import { ArrowClockwise16Regular } from '@fluentui/react-icons';
import { useSharedWithMe } from '../../../hooks/useSharedWithMe';
import { useRecentDocuments } from '../../../hooks/useRecentDocuments';
import { useRecentTeamMembers } from '../../../hooks/useRecentTeamMembers';
import { useUserTeams } from '../../../hooks/useUserTeams';

interface ActivityPanelProps {
  tenantId: string;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },

  headerTitle: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'rgba(0, 0, 0, 0.45)',
  },

  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    ...shorthands.borderRadius('4px'),
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(0, 0, 0, 0.4)',
    transitionProperty: 'background-color, color',
    transitionDuration: '0.15s',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.06)',
      color: 'rgba(0, 0, 0, 0.7)',
    },

    '&:disabled': {
      cursor: 'default',
      color: 'rgba(0, 0, 0, 0.2)',
    },
  },

  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '24px',
    minHeight: '200px',
  },

  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  columnHeader: {
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'rgba(0, 0, 0, 0.5)',
    ...shorthands.padding('0', '0', '8px', '0'),
    ...shorthands.borderBottom('1px', 'solid', 'rgba(0, 0, 0, 0.08)'),
  },

  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    ...shorthands.padding('10px', '12px'),
    ...shorthands.borderRadius('8px'),
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',

    '&:hover': {
      backgroundColor: 'rgba(95, 95, 210, 0.08)',
    },
  },

  itemPrimary: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(0, 0, 0, 0.85)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  itemSecondary: {
    fontSize: '12px',
    color: 'rgba(0, 0, 0, 0.5)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('24px'),
    color: 'rgba(0, 0, 0, 0.4)',
    fontSize: '13px',
    textAlign: 'center',
  },

  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('24px'),
  },
});

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
}

export function ActivityPanel({ tenantId }: ActivityPanelProps) {
  const styles = useStyles();
  const { documents: sharedDocuments, isLoading: sharedLoading, refetch: refetchShared } = useSharedWithMe({ tenantId });
  const { documents: recentDocuments, isLoading: documentsLoading, refetch: refetchDocuments } = useRecentDocuments({ tenantId, limit: 5 });
  const { teams } = useUserTeams({ tenantId });

  // Get the first team's members for "New Members" column
  const firstTeamId = teams.length > 0 ? teams[0].id : '';
  const { members, isLoading: membersLoading, refetch: refetchMembers } = useRecentTeamMembers({
    tenantId,
    teamId: firstTeamId,
    enabled: !!firstTeamId,
  });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const isLoading = sharedLoading || documentsLoading || membersLoading;

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([
      refetchShared(),
      refetchDocuments(),
      refetchMembers(),
    ]);
    setIsRefreshing(false);
  }, [refetchShared, refetchDocuments, refetchMembers]);

  return (
    <>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Activity</span>
        <Tooltip content="Refresh activity" relationship="label">
          <button
            type="button"
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={isLoading || isRefreshing}
            aria-label="Refresh activity"
          >
            <ArrowClockwise16Regular />
          </button>
        </Tooltip>
      </div>
      <div className={styles.container} data-testid="activity-panel">
      {/* Shared With You Column */}
      <div className={styles.column}>
        <div className={styles.columnHeader}>SHARED WITH YOU</div>
        {sharedLoading ? (
          <div className={styles.loadingState}>
            <Spinner size="tiny" />
          </div>
        ) : sharedDocuments.length === 0 ? (
          <div className={styles.emptyState}>No files shared recently</div>
        ) : (
          <div className={styles.itemsList}>
            {sharedDocuments.slice(0, 5).map((doc) => (
              <a
                key={doc.id}
                href={doc.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.item}
                style={{ textDecoration: 'none' }}
              >
                <span className={styles.itemPrimary}>{doc.name}</span>
                <span className={styles.itemSecondary}>
                  {doc.remoteItem.createdBy.user.displayName}
                </span>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* File Activity Column */}
      <div className={styles.column}>
        <div className={styles.columnHeader}>FILE ACTIVITY</div>
        {documentsLoading ? (
          <div className={styles.loadingState}>
            <Spinner size="tiny" />
          </div>
        ) : recentDocuments.length === 0 ? (
          <div className={styles.emptyState}>No recent activity</div>
        ) : (
          <div className={styles.itemsList}>
            {recentDocuments.map((doc) => (
              <a
                key={doc.id}
                href={doc.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.item}
                style={{ textDecoration: 'none' }}
              >
                <span className={styles.itemPrimary}>{doc.name}</span>
                <span className={styles.itemSecondary}>
                  {doc.lastModifiedBy && `${doc.lastModifiedBy} • `}
                  {doc.lastModifiedDateTime && formatRelativeTime(doc.lastModifiedDateTime)}
                </span>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* New Members Column */}
      <div className={styles.column}>
        <div className={styles.columnHeader}>NEW MEMBERS</div>
        {membersLoading ? (
          <div className={styles.loadingState}>
            <Spinner size="tiny" />
          </div>
        ) : members.length === 0 ? (
          <div className={styles.emptyState}>No new members</div>
        ) : (
          <div className={styles.itemsList}>
            {members.slice(0, 5).map((member) => (
              <div key={member.id} className={styles.item}>
                <span className={styles.itemPrimary}>{member.displayName}</span>
                <span className={styles.itemSecondary}>{member.email}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
